import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Select, Input, Space, Spin, Alert, Tag, Button, Tooltip,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  ReloadOutlined, SearchOutlined, ProjectOutlined, ThunderboltOutlined,
  DatabaseOutlined, DollarOutlined, RiseOutlined, CreditCardOutlined,
} from '@ant-design/icons';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const { RangePicker } = DatePicker;

const money = (v) => {
  if (v == null) return '-';
  const n = Math.round(v);
  if (n < 0) return <span style={{ color: '#cf1322' }}>{'-$' + Math.abs(n).toLocaleString()}</span>;
  return '$' + n.toLocaleString();
};
const moneyK = (v) => {
  if (v == null) return '-';
  const abs = Math.abs(v);
  let s;
  if (abs >= 1000000) s = '$' + (abs / 1000000).toFixed(2) + 'M';
  else if (abs >= 1000) s = '$' + (abs / 1000).toFixed(1) + 'K';
  else s = '$' + Math.round(abs).toLocaleString();
  return v < 0 ? '-' + s : s;
};
// Which commercial account code belongs to which line of the breakdown.
// Taken from the chart of accounts on the Commercial XERO Data tab.
const CODE_CATEGORY = {
  '41105': 'revenue',    // Package Income - Commercial
  '41301': 'rebate',     // Package Income _Rebate Commercial
  '52015': 'equipment',  // Cost of Sales - Commercial - Material
  '52016': 'equipment',  // Cost of Sales - Commercial - PW purchased Material
  '52017': 'install',    // Installers Cost - Commercial
  '52019': 'install',    // Installers Cost - Commercial - internal
  '52014': 'other',      // Cost of Sales - Commercial - other (Application/certificate)
  '65003': 'other',      // Freight - Commercial
};
const CATEGORY_LABEL = {
  revenue: 'Revenue', rebate: 'Rebate',
  equipment: 'Equipment Cost', install: 'Install', other: 'Other Cost',
  unmapped: 'Unmapped accounts',
};

// Sorters that keep blanks at the bottom instead of treating them as 0, which
// would bury real values under the projects that have no data.
const numSort = (key) => (a, b) => {
  const x = a[key], y = b[key];
  const xm = x == null, ym = y == null;
  if (xm && ym) return 0;
  if (xm) return 1;
  if (ym) return -1;
  return x - y;
};
const dateSort = (key) => (a, b) => {
  const x = a[key], y = b[key];
  if (!x && !y) return 0;
  if (!x) return 1;
  if (!y) return -1;
  return String(x).localeCompare(String(y));
};

// Project ids are written zero-padded to six digits everywhere else
const pid6 = (v) => String(v ?? '').padStart(6, '0');
const pct = (v) => v == null ? '-' : `${v.toFixed(1)}%`;

// Anchor for the period filter. Commercial has no closing date or welcome pack,
// so these are the nearest equivalents to residential's date fields.
const DATE_FIELDS = [
  { value: 'installationAt', label: 'Installation date' },
  { value: 'completedAt', label: 'Completion date' },
  { value: 'createdAt', label: 'Created date' },
];

// Fiscal year runs Jul-Jun, so its quarters land on the calendar quarter
// boundaries anyway (Q1 Jul-Sep, Q2 Oct-Dec, Q3 Jan-Mar, Q4 Apr-Jun) — the
// only thing the fiscal calendar changes here is where the year starts.
// Same helpers as RetailFinance, kept local rather than shared because that
// page's presets differ (it offers a full FY where this one offers YTD).
function fiscalQuarter(d) {
  const m = d.month();                                    // 0-based
  if (m >= 6 && m <= 8) return { startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { startM: 9, endM: 11 };
  if (m <= 2) return { startM: 0, endM: 2 };
  return { startM: 3, endM: 5 };
}
const fiscalYearStart = (d) => (d.month() >= 6
  ? dayjs().year(d.year()).month(6).startOf('month')
  : dayjs().year(d.year() - 1).month(6).startOf('month'));

const nowD = dayjs();
const curQ = fiscalQuarter(nowD);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = fiscalQuarter(prevQDate);
const fyStart = fiscalYearStart(nowD);
const lastFyStart = fyStart.subtract(1, 'year');

const DATE_PRESETS = [
  { label: 'This Month', value: [nowD.startOf('month'), nowD.endOf('month')] },
  { label: 'Last Month', value: [nowD.subtract(1, 'month').startOf('month'), nowD.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().month(curQ.startM).startOf('month'), dayjs().month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [prevQDate.month(prevQ.startM).startOf('month'), prevQDate.month(prevQ.endM).endOf('month')] },
  { label: `YTD (FY${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, nowD.endOf('day')] },
  { label: `Last FY (${lastFyStart.year()}/${fyStart.year()})`, value: [lastFyStart, fyStart.subtract(1, 'day').endOf('day')] },
];

function CommercialFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Opens on the current month. Clearing the picker drops the date filter
  // entirely, which is what the old "Filter by period" toggle did when off.
  const [range, setRange] = useState(() => [nowD.startOf('month'), nowD.endOf('month')]);
  const [dateField, setDateField] = useState('installationAt');
  const [state, setState] = useState();
  const [stage, setStage] = useState();
  const [systemType, setSystemType] = useState();
  const [purchaseMethod, setPurchaseMethod] = useState();
  const [search, setSearch] = useState('');
  // The parent-deal roll-up is still available on the API via groupBy=deal
  const groupBy = 'site';
  const [detailCategory, setDetailCategory] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { groupBy, dateField, state, stage, systemType, purchaseMethod, search: search || undefined };
      // The server compares these as plain YYYY-MM-DD strings against the same
      // shape, so both ends are inclusive.
      if (range?.[0] && range?.[1]) {
        params.start = range[0].format('YYYY-MM-DD');
        params.end = range[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/api/commercial-finance/summary', { params });
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load commercial data');
    }
    setLoading(false);
  }, [groupBy, dateField, state, stage, systemType, purchaseMethod, search, range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = data?.stats;
  const f = data?.filters || {};

  const columns = [
    { title: 'ID', dataIndex: 'projectId', width: 90, fixed: 'left', render: pid6 },
    {
      title: 'Project', dataIndex: 'name', width: 240, fixed: 'left',
      render: (v, r) => (
        <div style={{ lineHeight: 1.3 }}>
          <div>{v}</div>
          <div style={{ color: '#999', fontSize: 11 }}>
            {r.business && r.business !== v ? r.business : r.city}
          </div>
        </div>
      ),
    },
    {
      title: 'Stage', dataIndex: 'stage', width: 150,
      render: (v, r) => (
        <span>
          <Tag>{v}</Tag>
          {r.siteCount > 1 && <span style={{ color: '#999', fontSize: 11 }}>{r.siteCount} sites</span>}
        </span>
      ),
    },
    { title: 'State', dataIndex: 'state', width: 70 },
    { title: 'System', dataIndex: 'systemType', width: 150 },
    { title: 'PV kW', dataIndex: 'pvKw', width: 95, align: 'right', sorter: numSort('pvKw'), render: (v) => v ? v.toFixed(1) : '-' },
    { title: 'Batt kWh', dataIndex: 'battKwh', width: 105, align: 'right', sorter: numSort('battKwh'), render: (v) => v ? v.toFixed(1) : '-' },
    // Actuals first: Xero is complete, the CRM proposal is patchy
    {
      title: 'Actual Revenue', dataIndex: 'acctIncome', width: 140, align: 'right', sorter: numSort('acctIncome'),
      render: (v, r) => r.hasXero
        ? money(v)
        : <Tooltip title="No Xero rows attributed to this project"><span style={{ color: '#bbb' }}>&mdash;</span></Tooltip>,
    },
    {
      title: 'Actual GP', dataIndex: 'acctGp', width: 125, align: 'right', sorter: numSort('acctGp'),
      render: (v, r) => r.hasXero
        ? <b style={{ color: v < 0 ? '#cf1322' : '#05A677' }}>{money(v)}</b>
        : <span style={{ color: '#bbb' }}>&mdash;</span>,
    },
    {
      title: 'Actual GP%', dataIndex: 'acctGpPct', width: 115, align: 'right', sorter: numSort('acctGpPct'),
      render: (v) => v == null ? '-' : <span style={{ color: v < 0 ? '#cf1322' : '#05A677' }}>{pct(v)}</span>,
    },
    {
      title: 'Designed Revenue', dataIndex: 'designedRevenue', width: 135, align: 'right',
      render: (v) => v
        ? money(v)
        : <Tooltip title="Not filled in the CRM proposal"><span style={{ color: '#bbb' }}>&mdash;</span></Tooltip>,
    },
    {
      title: 'Designed GP', dataIndex: 'designedGp', width: 125, align: 'right',
      render: (v, r) => {
        if (!v) return <span style={{ color: '#bbb' }}>&mdash;</span>;
        return (
          <span>
            {money(v)}
            {r.designedSuspect && (
              <Tooltip title="Designed GP exceeds designed revenue - a CRM data error, so no GP% is shown">
                <Tag color="warning" style={{ marginLeft: 4, fontSize: 10 }}>?</Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: 'Designed GP%', dataIndex: 'designedGpPct', width: 120, align: 'right',
      render: (v) => v == null ? <span style={{ color: '#bbb' }}>&mdash;</span> : pct(v),
    },
    { title: 'Outstanding', dataIndex: 'paymentLeft', width: 120, align: 'right', render: money },
    { title: 'Installed', dataIndex: 'installationAt', width: 125, sorter: dateSort('installationAt') },
  ];

  // Per-project breakdown, laid out like the SH Residential expansion: the
  // category table on the left, the accounts and transactions behind whichever
  // category is clicked on the right.
  const expanded = (r) => {
    const txns = r.txns || [];
    const byCat = { revenue: [], rebate: [], equipment: [], install: [], other: [], unmapped: [] };
    for (const t of txns) {
      const cat = CODE_CATEGORY[t.code] || (t.isRevenue ? 'revenue' : 'unmapped');
      byCat[cat].push(t);
    }
    const sum = (list) => list.reduce((a, t) => a + (t.amount || 0), 0);
    const a = {
      revenue: sum(byCat.revenue), rebate: sum(byCat.rebate),
      equipment: sum(byCat.equipment), install: sum(byCat.install),
      other: sum(byCat.other), unmapped: sum(byCat.unmapped),
    };
    const aIncome = a.revenue + a.rebate;
    const aCost = a.equipment + a.install + a.other + a.unmapped;
    const aGp = aIncome - aCost;
    const aGpPct = aIncome ? (aGp / aIncome) * 100 : null;

    // The CRM proposal only carries a value and a margin, so the designed side
    // has a revenue and a total cost but no per-category split.
    const dRevenue = r.designedRevenue || null;
    const dGp = r.designedGp || null;
    const dCost = (dRevenue && dGp) ? dRevenue - dGp : null;
    const dGpPct = (dRevenue && dGp && !r.designedSuspect) ? (dGp / dRevenue) * 100 : null;

    const fmtVal = (v) => {
      if (v == null) return '-';
      const str = '$' + Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
      return v < 0 ? '-' + str : str;
    };
    const cs = { padding: '4px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12 };
    const ls = { padding: '4px 12px', fontSize: 12 };
    const ts = { ...cs, fontWeight: 700, borderTop: '1px solid #d9d9d9' };
    const tls = { ...ls, fontWeight: 700, borderTop: '1px solid #d9d9d9' };
    const gs = { ...cs, fontWeight: 700, borderTop: '2px solid #999' };
    const gls = { ...ls, fontWeight: 700, borderTop: '2px solid #999' };
    const ss = { padding: '6px 12px', fontWeight: 700, fontSize: 12, backgroundColor: '#fafafa' };
    const gpColor = (v) => v != null ? (v >= 0 ? '#05A677' : '#FA5252') : undefined;
    const clickable = (cat) => ({
      cursor: byCat[cat].length ? 'pointer' : 'default',
      backgroundColor: detailCategory === cat ? '#e6f4ff' : undefined,
    });
    const onCatClick = (cat) => {
      if (!byCat[cat].length) return;
      setDetailCategory(detailCategory === cat ? null : cat);
    };

    const row = (cat, label, n) => (
      <tr style={{ borderBottom: '1px solid #f0f0f0', ...clickable(cat) }} onClick={() => onCatClick(cat)}>
        <td style={ls}>
          {n}. {label}
          {byCat[cat].length > 0 && (
            <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>{byCat[cat].length} txns</span>
          )}
        </td>
        <td style={cs}>-</td>
        <td style={cs}>{byCat[cat].length ? fmtVal(a[cat]) : '-'}</td>
      </tr>
    );

    const detailTxns = detailCategory ? byCat[detailCategory] : null;
    const accounts = [];
    if (detailTxns) {
      const map = {};
      for (const t of detailTxns) {
        const key = t.account || t.code || 'Unknown';
        if (!map[key]) map[key] = { account: key, code: t.code, amount: 0, count: 0 };
        map[key].amount += t.amount || 0;
        map[key].count++;
      }
      accounts.push(...Object.values(map).sort((x, y) => Math.abs(y.amount) - Math.abs(x.amount)));
    }

    return (
      <Row gutter={16} style={{ padding: '4px 0' }}>
        <Col xs={24} md={12} lg={9}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Project Cost Breakdown</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #d9d9d9', backgroundColor: '#fafafa' }}>
                <th style={{ ...ls, fontWeight: 700 }}>Items</th>
                <th style={{ ...cs, fontWeight: 700 }}>Designed</th>
                <th style={{ ...cs, fontWeight: 700 }}>Actual</th>
              </tr>
            </thead>
            <tbody>
              <tr><td colSpan={3} style={ss}>Income</td></tr>
              {row('revenue', 'Revenue', 1)}
              {row('rebate', 'Rebate', 2)}
              <tr>
                <td style={tls}>Total Income</td>
                <td style={ts}>{fmtVal(dRevenue)}</td>
                <td style={ts}>{txns.length ? fmtVal(aIncome) : '-'}</td>
              </tr>
              <tr><td colSpan={3} style={{ ...ss, paddingTop: 10 }}>Expenses</td></tr>
              {row('equipment', 'Equipment Cost', 1)}
              {row('install', 'Install', 2)}
              {row('other', 'Other Cost', 3)}
              {byCat.unmapped.length > 0 && row('unmapped', 'Unmapped accounts', 4)}
              <tr>
                <td style={tls}>Total Expenses</td>
                <td style={ts}>{fmtVal(dCost)}</td>
                <td style={ts}>{txns.length ? fmtVal(aCost) : '-'}</td>
              </tr>
              <tr>
                <td style={gls}>GP</td>
                <td style={{ ...gs, color: gpColor(dGp) }}>{fmtVal(dGp)}</td>
                <td style={{ ...gs, color: gpColor(aGp) }}>{txns.length ? fmtVal(aGp) : '-'}</td>
              </tr>
              <tr>
                <td style={{ ...ls, fontWeight: 700 }}>GP%</td>
                <td style={{ ...cs, fontWeight: 700, color: gpColor(dGpPct) }}>
                  {dGpPct != null ? `${dGpPct.toFixed(1)}%` : '-'}
                </td>
                <td style={{ ...cs, fontWeight: 700, color: gpColor(aGpPct) }}>
                  {aGpPct != null ? `${aGpPct.toFixed(1)}%` : '-'}
                </td>
              </tr>
            </tbody>
          </table>
          {!txns.length && (
            <div style={{ color: '#999', fontSize: 12, padding: '8px 12px' }}>
              No Xero transactions reference "Commercial {pid6(r.projectId)}".
            </div>
          )}
          {dRevenue && (
            <div style={{ color: '#999', fontSize: 11, padding: '6px 12px' }}>
              The CRM proposal carries only a value and a margin, so Designed has no per-category split.
            </div>
          )}
        </Col>
        {detailTxns && (
          <Col xs={24} md={12} lg={15}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              {CATEGORY_LABEL[detailCategory]} &mdash; Detail
            </div>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#54A2CC', marginBottom: 4 }}>By account</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
              <tbody>
                {accounts.map((acc) => (
                  <tr key={acc.account} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '3px 8px 3px 0', color: '#666' }}>
                      {acc.account}
                      <span style={{ color: '#bbb', marginLeft: 6 }}>{acc.code}</span>
                      <span style={{ color: '#bbb', marginLeft: 6 }}>{acc.count} txns</span>
                    </td>
                    <td style={{ padding: '3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtVal(acc.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#05A677', marginBottom: 4 }}>
              Transactions ({detailTxns.length})
            </div>
            <Table
              size="small" pagination={detailTxns.length > 12 ? { pageSize: 12 } : false}
              rowKey={(t, i) => i} dataSource={detailTxns}
              columns={[
                { title: 'Date', dataIndex: 'date', width: 100 },
                { title: 'Source', dataIndex: 'source', width: 140 },
                { title: 'Reference', dataIndex: 'reference', width: 210, ellipsis: true },
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                {
                  title: 'Amount', width: 110, align: 'right',
                  render: (_, t) => fmtVal(t.amount),
                },
              ]}
            />
          </Col>
        )}
      </Row>
    );
  };

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Shinehub Commercial Projects</h1>
        <p>Live-stage commercial projects &mdash; CRM proposal vs Xero accounts</p>
      </div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Space wrap size="small">
          <Input
            prefix={<SearchOutlined />} placeholder="Search project, business, address, PO"
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ width: 250 }} size="small" allowClear
          />
          <Select size="small" style={{ width: 165 }} value={dateField} onChange={setDateField} options={DATE_FIELDS} />
          <RangePicker
            size="small" style={{ width: 250 }} allowClear
            format="DD MMM YYYY" presets={DATE_PRESETS}
            value={range} onChange={setRange}
          />
          <Select size="small" style={{ width: 150 }} allowClear placeholder="All stages"
            value={stage} onChange={setStage} options={(f.stages || []).map(v => ({ value: v, label: v }))} />
          <Select size="small" style={{ width: 110 }} allowClear placeholder="All states"
            value={state} onChange={setState} options={(f.states || []).map(v => ({ value: v, label: v }))} />
          <Select size="small" style={{ width: 175 }} allowClear placeholder="All system types"
            value={systemType} onChange={setSystemType} options={(f.systemTypes || []).map(v => ({ value: v, label: v }))} />
          <Select size="small" style={{ width: 140 }} allowClear placeholder="All purchase types"
            value={purchaseMethod} onChange={setPurchaseMethod} options={(f.purchaseMethods || []).map(v => ({ value: v, label: v }))} />
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
        </Space>
      </Card>

      <Spin spinning={loading}>
        {data && (data.unresolved.rows > 0 || data.outOfScope.projects > 0 || data.legacy.projects > 0) && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message="Commercial spend not shown in the table above"
            description={
              <span style={{ fontSize: 12 }}>
                {data.outOfScope.projects > 0 && (
                  <div>
                    <b>{data.outOfScope.projects}</b> commercial projects carry Xero activity but sit outside the
                    {' '}{data.meta.inScopeStages.length} live stages — income {money(data.outOfScope.income)},
                    cost {money(data.outOfScope.cost)}.
                  </div>
                )}
                {data.legacy.projects > 0 && (
                  <div>
                    <b>{data.legacy.projects}</b> jobs are still filed under a residential project id (early
                    commercial work predating the migration) — income {money(data.legacy.income)},
                    cost {money(data.legacy.cost)}.
                    {data.legacy.items.length > 0 && (
                      <span style={{ color: '#666' }}>
                        {' '}Largest: {data.legacy.items.slice(0, 3).map(i => `${i.projectId} ${i.name}`).join(', ')}.
                      </span>
                    )}
                  </div>
                )}
                {data.unresolved.rows > 0 && (
                  <div>
                    <b>{data.unresolved.rows}</b> rows carry no recognisable project id
                    ({data.unresolved.samples.slice(0, 3).map(x => `"${x.reference}"`).join(', ')}) —
                    income {money(data.unresolved.income)}, cost {money(data.unresolved.cost)}.
                  </div>
                )}
                {data.viaDescriptionRows > 0 && (
                  <div>
                    <b>{data.viaDescriptionRows}</b> rows were split by a line-level
                    <b> SHID:xxxxxx</b> in the description, which overrides the invoice reference.
                  </div>
                )}
                {data.weakMatchRows > 0 && (
                  <div>
                    <b>{data.weakMatchRows}</b> rows matched only on a short zero-padded id
                    (e.g. "SHC-00561") rather than a six-digit one — weaker attribution.
                  </div>
                )}
                {data.multiSiteRows > 0 && (
                  <div>
                    <b>{data.multiSiteRows}</b> rows name more than one site and are attributed to the first.
                  </div>
                )}
              </span>
            }
          />
        )}

        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {[
            {
              title: 'Projects', value: s?.projectCount,
              icon: <ProjectOutlined style={{ color: '#54A2CC' }} />,
              sub: s ? `${s.withXeroActuals} with Xero actuals` : '',
            },
            {
              title: 'Total PV', value: s?.totalPvKw, suffix: 'kW',
              icon: <ThunderboltOutlined style={{ color: '#f5b759' }} />, sub: '',
            },
            {
              title: 'Total Battery', value: s?.totalBattKwh, suffix: 'kWh',
              icon: <DatabaseOutlined style={{ color: '#845EC2' }} />, sub: '',
            },
            {
              title: 'Designed Revenue', value: s?.designedRevenue, isMoney: true,
              icon: <DollarOutlined style={{ color: '#54A2CC' }} />,
              sub: s ? `${s.withDesignedRevenue}/${s.projectCount} filled in` : '',
            },
            {
              title: 'Actual Revenue', value: s?.acctIncome, isMoney: true,
              icon: <DollarOutlined style={{ color: '#05A677' }} />,
              sub: s ? `cost ${moneyK(s.acctCost)}` : '',
            },
          ].map((k) => (
            <Col key={k.title} flex="1 1 20%" style={{ minWidth: 180 }}>
              <Card size="small" bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={k.title}
                  value={k.isMoney ? moneyK(k.value) : (k.value ?? '-')}
                  prefix={k.icon}
                  suffix={k.suffix}
                  valueStyle={{ fontSize: 20 }}
                />
                <div style={{ color: '#999', fontSize: 11, minHeight: 16 }}>{k.sub}</div>
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {[
            {
              title: 'Designed GP', value: moneyK(s?.designedGp),
              icon: <RiseOutlined style={{ color: '#54A2CC' }} />,
              sub: s ? `${s.withDesignedGp}/${s.projectCount} filled${s.designedSuspectCount ? ' - ' + s.designedSuspectCount + ' suspect' : ''}` : '',
            },
            {
              title: 'Actual GP', value: moneyK(s?.acctGp),
              icon: <RiseOutlined style={{ color: '#05A677' }} />,
              sub: s?.acctGpPct != null ? `GP ${pct(s.acctGpPct)}` : '',
            },
            {
              title: 'Outstanding Payment', value: moneyK(s?.paymentLeft),
              icon: <CreditCardOutlined style={{ color: '#f5b759' }} />,
              // Against actual revenue, since that is the accounting basis the
              // rest of the page reports on
              sub: (s && s.acctIncome) ? `${pct((s.paymentLeft / s.acctIncome) * 100)} of actual revenue` : '',
            },
          ].map((k) => (
            <Col key={k.title} flex="1 1 33%" style={{ minWidth: 220 }}>
              <Card size="small" bodyStyle={{ padding: 12 }}>
                <Statistic title={k.title} value={k.value} prefix={k.icon} valueStyle={{ fontSize: 20 }} />
                <div style={{ color: '#999', fontSize: 11, minHeight: 16 }}>{k.sub}</div>
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          size="small"
          title={data
            ? (data.meta.groupBy === 'deal'
                ? `${data.stats.projectCount} parent deals`
                : `${data.stats.projectCount} projects in ${data.meta.inScopeStages.length} live stages`)
            : 'Projects'}
        >
          <Table
            rowKey="projectId"
            dataSource={data?.projects || []}
            columns={columns}
            size="small"
            pagination={{
              // defaultPageSize, not pageSize: passing pageSize makes it a
              // controlled prop, and without an onShowSizeChange handler the
              // user's choice was being overwritten on every re-render
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }}
            scroll={{ x: 1840 }}
            expandable={{ expandedRowRender: expanded }}
            summary={(rows) => {
              const t = (k) => rows.reduce((acc, r) => acc + (r[k] || 0), 0);
              const inc = t('acctIncome'), gp = t('acctGp');
              const num = (v, digits = 0) => v ? v.toFixed(digits) : '-';
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                    {/* 0: the expand-icon column, which antd does not fill in itself */}
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{rows.length} projects</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />
                    <Table.Summary.Cell index={6} align="right">{num(t('pvKw'), 1)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">{num(t('battKwh'), 1)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">{money(inc)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">{money(gp)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">{inc ? pct((gp / inc) * 100) : '-'}</Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">{money(t('designedRevenue'))}</Table.Summary.Cell>
                    <Table.Summary.Cell index={12} align="right">{money(t('designedGp'))}</Table.Summary.Cell>
                    <Table.Summary.Cell index={13} />
                    <Table.Summary.Cell index={14} align="right">{money(t('paymentLeft'))}</Table.Summary.Cell>
                    <Table.Summary.Cell index={15} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      </Spin>
    </PageLayout>
  );
}

export default CommercialFinance;
